/**
 * WishlistContext — Gestion de la liste de souhaits
 * - Auth requis pour toutes les opérations
 * - Optimistic updates pour une UX fluide
 */
import {
  createContext, useContext, useReducer, useEffect, useCallback,
} from 'react'
import { wishlistApi } from '../services/api'
import { liste } from '../utils/listes'
import { useAuth } from './AuthContext'

const WishlistContext = createContext(null)

const initial = { ids: new Set(), count: 0, loading: false }

function reducer(state, { type, payload }) {
  switch (type) {
    case 'SET_IDS':   return { ...state, ids: new Set(payload.ids), count: payload.count, loading: false }
    case 'ADD_ID':    return { ...state, ids: new Set([...state.ids, payload]), count: state.count + 1 }
    case 'REMOVE_ID': {
      const next = new Set(state.ids)
      next.delete(payload)
      return { ...state, ids: next, count: Math.max(0, state.count - 1) }
    }
    case 'SET_LOADING': return { ...state, loading: payload }
    case 'RESET':       return initial
    default:            return state
  }
}

export function WishlistProvider({ children }) {
  const { isAuth, isLoading: authLoading } = useAuth()
  const [state, dispatch] = useReducer(reducer, initial)

  /* Charge la wishlist à la connexion */
  useEffect(() => {
    if (authLoading) return
    if (!isAuth) { dispatch({ type: 'RESET' }); return }

    dispatch({ type: 'SET_LOADING', payload: true })
    wishlistApi.list()
      .then(res => {
        const ids = liste(res.data, 'wishlist').map(p => p.id)
        dispatch({ type: 'SET_IDS', payload: { ids, count: ids.length } })
      })
      .catch(() => dispatch({ type: 'SET_LOADING', payload: false }))
  }, [isAuth, authLoading])

  const toggle = useCallback(async (product) => {
    if (!isAuth) return false // caller doit rediriger vers login

    const productId = product?.id ?? product
    const isInList  = state.ids.has(productId)

    // Optimistic update
    dispatch({ type: isInList ? 'REMOVE_ID' : 'ADD_ID', payload: productId })

    try {
      if (isInList) {
        await wishlistApi.remove(productId)
      } else {
        await wishlistApi.add(productId)
      }
      return !isInList
    } catch {
      // Rollback
      dispatch({ type: isInList ? 'ADD_ID' : 'REMOVE_ID', payload: productId })
      return isInList
    }
  }, [isAuth, state.ids])

  const isInWishlist = useCallback((productId) => state.ids.has(productId), [state.ids])

  return (
    <WishlistContext.Provider value={{
      wishlistIds:  state.ids,
      wishlistCount:state.count,
      loading:      state.loading,
      toggle,
      isInWishlist,
    }}>
      {children}
    </WishlistContext.Provider>
  )
}

export function useWishlist() {
  const ctx = useContext(WishlistContext)
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider')
  return ctx
}
